import { Flex, Spin, Table, TableColumnType, Tooltip } from 'antd'
import dayjs from 'dayjs'
import { filter, isEmpty, isNil, sortBy } from 'lodash-es'
import isUndefined from 'lodash-es/isUndefined'
import { FunctionComponent, ReactNode, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { nameof } from 'ts-simple-nameof'
import Blur from '@/components/common/blur'
import { Duration } from '@/components/common/duration-select'
import PostImage, {
  PostImageWrapper,
} from '@/components/common/image/post-image'
import AnalyzeFeedback from '@/components/kol/analyze-feedback'
import KolDetailDataBlock from '@/components/kol/common/kol-detail-data-block'
import KolDetailDataBlockSubHeader from '@/components/kol/common/kol-detail-data-block-sub-header'
import KolDetailStatistic from '@/components/kol/common/kol-detail-statistic'
import Blocker from '@/components/kol/detail/blocker'
import { NoData } from '@/components/kol/detail/no-data'
import useKolAnalyze from '@/components/kol/hooks/use-kol-analyze'
import SocialMediaPostModal, {
  SocialMediaPost,
} from '@/components/modal/social-media-post-modal'
import mockSponsoredPosts from '@/constants/mock-data/sponsored-posts'
import useIsPublicDetailPage from '@/hooks/use-is-public-detail-page'
import { useIntl } from '@/i18n/hooks/use-intl'
import { FeedbackType } from '@/types/api/feedback'
import { KolSchemaModel } from '@/types/schema/kol-schema'
import {
  ampli,
  ClickReportSponsoredPostAccuracyProperties,
} from '@/utils/ampli'
import { Platform, PlatformShortcode } from '@/utils/convert-platform'
import { getImageUrlFromApiServer } from '@/utils/post/get-image-url-from-api-server'

export interface SponsoredPost {
  id: string
  title: string
  url?: string
  imageUrls: string[]
  publishedAt: string
  interactiveRate: number
  viewRate: number
  embedInfo?: SocialMediaPost
}

interface SponsoredCollaborationProps {
  duration: Duration.ThreeMonths | Duration.SixMonths
  isFetchingKolInfo: boolean
  isKolInfoLocked: boolean
  kolInfo?: KolSchemaModel
  loading?: boolean
  platform: Platform
  showMockData?: boolean
}

const SponsoredCollaboration: FunctionComponent<
  SponsoredCollaborationProps
> = ({
  duration,
  isKolInfoLocked,
  isFetchingKolInfo,
  kolInfo,
  loading,
  platform,
  showMockData,
}) => {
  const { formatMessage, formatNumber, formatDate } = useIntl()
  const isPublicDetailPage = useIsPublicDetailPage()
  const [selectedPost, setSelectedPost] = useState<SocialMediaPost>()
  const containerRef = useRef<HTMLDivElement>(null)

  const { analyze, isFetching: isFetchingKolAnalyze } = useKolAnalyze({
    kolId: kolInfo?.id,
    kolDcIdHashed: kolInfo?.dcIdHashed,
    isKolInfoLocked,
    platform: platform.code,
  })

  const isFetching = isFetchingKolInfo || isFetchingKolAnalyze
  const posts = analyze?.filter((kolAnalyze) => {
    const diff = dayjs().diff(dayjs(kolAnalyze.publishedAt), 'months', true)

    return diff <= (duration === Duration.ThreeMonths ? 3 : 6)
  })

  const sponsorPostRatio = useMemo(() => {
    const kpi = kolInfo?.kpi.find((kpi) => kpi.platform === platform.shortcode)
    if (isUndefined(kpi)) {
      return null
    }

    return duration === Duration.ThreeMonths
      ? kpi.sponsorPostRatio3m
      : kpi.sponsorPostRatio6m
  }, [kolInfo, platform, duration])

  const sponsoredPosts: SponsoredPost[] = isKolInfoLocked
    ? mockSponsoredPosts
    : sortBy(
        filter(posts, (kolAnalyze) => kolAnalyze.isAdvertorial),
        (kolAnalyze) => kolAnalyze.interactiveRate,
      )
        .reverse()
        .map((kolAnalyze): SponsoredPost => {
          return {
            id: kolAnalyze.postId,
            title: kolAnalyze.titleContent,
            url: kolAnalyze.url,
            imageUrls: [
              kolAnalyze.thumbnail,
              getImageUrlFromApiServer(
                platform.code,
                kolAnalyze.crawledId,
                kolAnalyze.postId,
                kolAnalyze.igPostId,
              ),
            ],
            publishedAt: kolAnalyze.publishedAt,
            interactiveRate: kolAnalyze.interactiveRate,
            viewRate: kolAnalyze.viewRate,
            embedInfo: {
              crawledID: kolAnalyze.crawledId,
              postID: kolAnalyze.postId,
              url: kolAnalyze.url,
              platformCode: platform.code,
              platformTitle: platform.title,
              postType: undefined,
              kolId: kolInfo?.id,
              kolDcIdHashed: kolInfo?.dcIdHashed,
              kolName: kolInfo?.name,
            },
          }
        })

  const columns: TableColumnType<SponsoredPost>[] = [
    {
      width: 30,
      dataIndex: nameof<SponsoredPost>((s) => s.id),
      render: (_text, _post, index) => <PostIndex>#{index + 1}</PostIndex>,
    },
    {
      width: 110,
      dataIndex: nameof<SponsoredPost>((s) => s.imageUrls),
      render: (imageUrls: SponsoredPost['imageUrls'], post) =>
        kolInfo?.id && (
          <PostImage
            alt={post.title}
            borderRadius={3}
            height={60}
            kolUuid={kolInfo.id}
            thumbnailUrls={imageUrls}
            width={100}
          />
        ),
    },
    {
      dataIndex: nameof<SponsoredPost>((s) => s.title),
      ellipsis: true,
      render: (title: SponsoredPost['title'], post) => (
        <Flex vertical gap={3}>
          <Tooltip
            overlayInnerStyle={{
              maxHeight: '186px', // 8 行文字 22px(line-height) * 8 + 12px (padding)  - 2px , 2px 是 避免第九行文字出現一些
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 8,
              WebkitBoxOrient: 'vertical',
            }}
            title={title.trim()}
          >
            <PostTitle>{title}</PostTitle>
          </Tooltip>
          <PublishedDate>
            {formatDate(post.publishedAt, 'dateTimeShort')}
          </PublishedDate>
        </Flex>
      ),
    },
    {
      width: 80,
      title: (
        <ColumnTitle>
          {formatMessage({
            id: 'kol:sponsored_posts_analysis_interactive_rate_average',
          })}
        </ColumnTitle>
      ),
      dataIndex: nameof<SponsoredPost>((s) => s.interactiveRate),
      render: (interactiveRate: SponsoredPost['interactiveRate']) => (
        <Rate>
          {formatNumber(interactiveRate, {
            isPercentageDisplay: true,
            maximumFractionDigits: 2,
          })}
        </Rate>
      ),
    },
    ...(platform.shortcode === PlatformShortcode.Threads
      ? []
      : [
          {
            width: 80,
            title: (
              <ColumnTitle>
                {formatMessage({
                  id: 'kol:sponsored_posts_analysis_view_rate_average',
                })}
              </ColumnTitle>
            ),
            dataIndex: nameof<SponsoredPost>((s) => s.viewRate),
            render: (viewRate: SponsoredPost['viewRate']) => (
              <Rate>
                {formatNumber(viewRate, { isPercentageDisplay: true })}
              </Rate>
            ),
          },
        ]),
  ]

  return (
    <KolDetailDataBlock
      hoverableExtraContent
      colSpan={12}
      header={{
        poweredByAI: true,
        tooltip: {
          title: formatMessage({ id: 'kol:sponsored_posts_analysis_tooltip' }),
        },
        title: formatMessage(
          {
            id: 'kol:sponsored_collaboration_data_block_title',
          },
          { platformTitle: platform.title },
        ),
        extraContent: (
          <AnalyzeFeedback
            feedbackEvent={(
              eventProps: ClickReportSponsoredPostAccuracyProperties,
            ): void => {
              ampli.clickReportSponsoredPostAccuracy(eventProps)
            }}
            feedbackType={FeedbackType.Advertorial}
            kolInfo={kolInfo}
            platform={platform.shortcode}
          />
        ),
      }}
      kolId={kolInfo?.id}
      kolMetrics='Sponsored post analysis'
      kolName={kolInfo?.name}
      platform={platform.shortcode}
      ref={containerRef}
    >
      <Flex vertical gap={30} style={{ height: '100%' }}>
        <KolDetailStatistic
          loading={loading ?? isFetching}
          title={formatMessage({
            id: 'kol:sponsored_collaboration_data_block_sponsored_coverage_title',
          })}
          value={((): ReactNode => {
            if (isKolInfoLocked) {
              return (
                <Blur activate={!showMockData}>
                  {formatNumber(0.3, { isPercentageDisplay: true })}
                </Blur>
              )
            }

            if (isNil(sponsorPostRatio) || sponsorPostRatio === 0) {
              return null
            }

            return formatNumber(sponsorPostRatio, {
              isPercentageDisplay: true,
            })
          })()}
        />
        <Flex vertical gap={10} style={{ flex: 1 }}>
          <KolDetailDataBlockSubHeader
            title={formatMessage({
              id: 'kol:sponsored_collaboration_data_block_top_three_posts_sub_header_title',
            })}
          />
          {((): ReactNode => {
            if (isFetching) {
              return (
                <Flex align='center' justify='center' style={{ flex: 1 }}>
                  <Spin />
                </Flex>
              )
            }

            if (isEmpty(sponsoredPosts)) {
              return <NoData />
            }

            return (
              <Spin indicator={<Blocker />} spinning={isPublicDetailPage}>
                <Blur activate={isKolInfoLocked && !showMockData}>
                  <StyledTable
                    $isKolInfoLocked={isKolInfoLocked}
                    columns={columns}
                    dataSource={sponsoredPosts.slice(0, 3)}
                    pagination={false}
                    onRow={(record: SponsoredPost): { onClick: () => void } => {
                      return {
                        onClick: (): void => {
                          // 只有 mockData 會沒有 embedInfo
                          if (!record.embedInfo) {
                            return
                          }

                          setSelectedPost(record.embedInfo)
                        },
                      }
                    }}
                  />
                </Blur>
              </Spin>
            )
          })()}
        </Flex>
      </Flex>
      {selectedPost && (
        <SocialMediaPostModal
          containerRef={containerRef}
          from='KOL Detail'
          post={selectedPost}
          onCancel={(): void => setSelectedPost(undefined)}
        />
      )}
    </KolDetailDataBlock>
  )
}

const ColumnTitle = styled.div`
  color: ${({ theme }): string => theme.colors.base.grey.grey3};
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
`

const PostIndex = styled.div`
  color: ${({ theme }): string => theme.colors.base.grey.grey1};
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
  text-wrap: nowrap;
`

const PostTitle = styled.div`
  color: ${({ theme }): string => theme.colors.base.grey.grey1};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
  overflow: hidden;
  text-overflow: ellipsis;
`

const PublishedDate = styled.p`
  color: ${({ theme }): string => theme.colors.base.grey.grey3};
  font-style: normal;
  font-weight: 400;
  font-size: 13px;
  line-height: 22px;
  margin: 0;
`

const Rate = styled.div`
  color: ${({ theme }): string => theme.colors.base.grey.grey1};
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 140%;
`

const StyledTable = styled(Table)<{ $isKolInfoLocked: boolean }>`
  ${PostImageWrapper} {
    background: ${({ theme }): string => theme.colors.background.grey};
    img {
      object-position: center;
    }
  }

  .ant-table-row {
    cursor: ${({ $isKolInfoLocked }): string =>
      $isKolInfoLocked ? 'default' : 'pointer'};
  }

  .ant-table-placeholder > td {
    border-bottom: none;
  }

  .ant-table-thead > tr > th {
    padding: 0;
    border: none;
  }

  .ant-table-tbody > tr > td {
    padding: 12px 10px 12px 0;
  }
`

export default SponsoredCollaboration
